/**
 * Scheda giocatore — Carichi (V69). In cima dose e ACWR; sotto la finestra la
 * dose per attributo, la linea della dose nel tempo (solo i periodi con un carico) e gli
 * attributi divisi per tipologia, ognuna richiudibile. Il filtro per categoria vale
 * anche per la dose in cima e per la linea.
 */

import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, Cell, Line, LineChart, XAxis, YAxis } from 'recharts';
import { ChartColumnIcon, ChartLineIcon, ChevronRightIcon, GaugeIcon } from 'lucide-react';
import {
  ETICHETTA_FAMIGLIA_ATTRIBUTO,
  FAMIGLIE_ATTRIBUTO,
  FINESTRE_CARICO,
  type FamigliaAttributo,
} from '@calcio/shared';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart';
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@/components/ui/empty';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { StatoCaricamento } from '../comuni/Testata.js';
import { useDati } from '../comuni/dati.js';
import { IconaFamiglia } from '../comuni/famiglie.js';
import { NonTrovata } from '../navigazione/NonTrovata.js';
import { SezioneGrafici } from '../comuni/Riquadri.js';
import { coloreSerie, pallinoSeSolo } from '../valutazioni/graficiConfronto.js';
import { CampoIntervallo } from '../comuni/CampoIntervallo.js';
import { type AttributoCarico, type CarichiGiocatore as Dati } from './carichi.js';
import { RiassuntoPeriodo } from './RiassuntoPeriodo.js';
import {
  ETICHETTA_MODO,
  MODO_FINESTRA_DEFAULT,
  type FiltroFamigliaAttributo,
  type ModoFinestra,
  attributiPerGrafico,
  doseDelFiltro,
  etichettaAsse,
  famigliePresenti,
  filtraAttributi,
  percorsoCarichiGiocatore,
  righeDoseGiocatore,
  serieDelFiltro,
} from './serieSquadra.js';

const CONFIG_ATTRIBUTI: ChartConfig = {
  ua: { label: 'Dose', color: 'var(--chart-1)' },
};

const CONFIG_DOSE: ChartConfig = {
  dose: { label: 'Dose', color: 'var(--chart-1)' },
};

export function CarichiGiocatore() {
  const { id } = useParams<{ id: string }>();
  const [modo, setModo] = useState<ModoFinestra>(MODO_FINESTRA_DEFAULT);
  const [da, setDa] = useState('');
  const [a, setA] = useState('');
  const [famiglia, setFamiglia] = useState<FiltroFamigliaAttributo>('tutti');
  const percorso = percorsoCarichiGiocatore(id!, modo, da, a);
  const { dati, errore, stato } = useDati<Dati>(percorso, { tieni: true });

  if (stato === 403 || stato === 404) return <NonTrovata />;
  if (!dati) return <StatoCaricamento errore={errore} />;

  const conDose = attributiPerGrafico(dati.attributi);
  const famiglie = famigliePresenti(conDose);
  const filtro: FiltroFamigliaAttributo =
    famiglia === 'tutti' || famiglie.includes(famiglia) ? famiglia : 'tutti';
  const attributi = filtraAttributi(conDose, filtro);
  const settimanale = modo === 'stagione';
  // Solo i giorni (o le settimane) con un carico: gli zeri non dicono nulla.
  const righe = righeDoseGiocatore(
    dati.asse,
    serieDelFiltro(dati.serieDose, dati.serieDosePerFamiglia, filtro),
  ).filter((r) => r.dose > 0);
  const tick = (chiave: string) => etichettaAsse(chiave, settimanale);
  const haDoseNelTempo = righe.length > 0;

  return (
    <div className="flex flex-col gap-8">
      {/* Il riassunto del periodo, in evidenza: e la prima cosa che si cerca. */}
      <RiassuntoPeriodo
        dose={doseDelFiltro(dati.doseUa, dati.attributi, filtro)}
        totale={dati.doseUa}
        famiglia={filtro}
        acwr={dati.acwr}
        avviso={
          dati.serieSottoTarget
            ? 'Tre sedute consecutive sotto il RPE target negli ultimi 14 giorni.'
            : undefined
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        <ToggleGroup
          type="single"
          variant="outline"
          aria-label="Finestra"
          value={modo === 'personalizzato' ? '' : modo}
          onValueChange={(v) => v && setModo(v as ModoFinestra)}
        >
          {FINESTRE_CARICO.map((f) => (
            <ToggleGroupItem key={f} value={f}>
              {ETICHETTA_MODO[f]}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
        <CampoIntervallo
          id="carichi-giocatore-intervallo"
          da={dati.da}
          a={dati.a}
          onCambia={(dal, al) => {
            setDa(dal);
            setA(al);
            setModo('personalizzato');
          }}
        />
      </div>

      <SezioneGrafici
        titolo="Categoria"
        Icona={ChartColumnIcon}
        azione={
          famiglie.length > 0 && (
            <ToggleGroup
              type="single"
              variant="outline"
              className="flex-wrap"
              value={filtro}
              onValueChange={(v) => v && setFamiglia(v as FiltroFamigliaAttributo)}
            >
              <ToggleGroupItem value="tutti">Tutti</ToggleGroupItem>
              {famiglie.map((f) => (
                <ToggleGroupItem key={f} value={f}>
                  {ETICHETTA_FAMIGLIA_ATTRIBUTO[f]}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          )
        }
      >
        {attributi.length === 0 ? (
          <Empty>
            <EmptyHeader>
              <EmptyTitle>Nessun carico</EmptyTitle>
              <EmptyDescription>Nessuna dose per attributo in questa finestra.</EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (
          <ChartContainer config={CONFIG_ATTRIBUTI} className="aspect-auto h-80 w-full">
            <BarChart data={attributi} margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
              <CartesianGrid vertical={false} />
              <XAxis
                dataKey="nome"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                interval={0}
                angle={-40}
                textAnchor="end"
                height={80}
              />
              <YAxis allowDecimals={false} tickLine={false} axisLine={false} width={40} />
              <ChartTooltip cursor={false} content={<ChartTooltipContent indicator="dot" />} />
              <Bar dataKey="ua" radius={4}>
                {attributi.map((x) => (
                  <Cell
                    key={x.attributoId}
                    fill={coloreSerie(conDose.findIndex((y) => y.attributoId === x.attributoId))}
                  />
                ))}
              </Bar>
            </BarChart>
          </ChartContainer>
        )}
      </SezioneGrafici>

      <SezioneGrafici titolo="Dose nel tempo" Icona={ChartLineIcon}>
        {!haDoseNelTempo ? (
          <Empty>
            <EmptyHeader>
              <EmptyTitle>Nessun carico</EmptyTitle>
              <EmptyDescription>Nessuna dose nel tempo in questa finestra.</EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (
          <ChartContainer config={CONFIG_DOSE} className="aspect-auto h-64 w-full">
            <LineChart data={righe} margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
              <CartesianGrid vertical={false} />
              <XAxis
                dataKey="chiave"
                tickFormatter={tick}
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                minTickGap={24}
              />
              <YAxis allowDecimals={false} tickLine={false} axisLine={false} width={40} />
              <ChartTooltip
                content={
                  <ChartTooltipContent
                    indicator="dot"
                    labelFormatter={(v) => (typeof v === 'string' ? tick(v) : null)}
                  />
                }
              />
              <Line
                dataKey="dose"
                type="monotone"
                stroke="var(--color-dose)"
                strokeWidth={3}
                dot={pallinoSeSolo(righe, 'dose')}
              />
            </LineChart>
          </ChartContainer>
        )}
      </SezioneGrafici>

      <SezioneGrafici titolo="Attributi" Icona={GaugeIcon}>
        <div className="flex flex-col gap-3">
          {FAMIGLIE_ATTRIBUTO.map((f) => (
            <GruppoAttributi
              key={f}
              famiglia={f}
              righe={dati.attributi.filter((x) => x.famiglia === f)}
            />
          ))}
        </div>
      </SezioneGrafici>
    </div>
  );
}

/** Una tipologia chiusa mostra il suo totale; aperta, attributo per attributo. */
function GruppoAttributi({
  famiglia,
  righe,
}: {
  famiglia: FamigliaAttributo;
  righe: AttributoCarico[];
}) {
  if (righe.length === 0) return null;
  // Sui centesimi, interi: la somma dei decimali non lascia residui.
  const ua = Math.round(righe.reduce((t, x) => t + x.centesimiUa, 0) / 100);
  const percentuale = righe.reduce((t, x) => t + x.percentuale, 0);
  return (
    <Collapsible className="rounded-lg border">
      <CollapsibleTrigger asChild>
        <Button variant="ghost" className="group h-auto w-full justify-between px-3 py-2">
          <span className="flex items-center gap-2">
            <ChevronRightIcon className="transition-transform group-data-[state=open]:rotate-90" />
            <IconaFamiglia famiglia={famiglia} aria-hidden />
            {ETICHETTA_FAMIGLIA_ATTRIBUTO[famiglia]}
          </span>
          <span className="text-muted-foreground tabular-nums">
            {ua} UA · {Math.round(percentuale)}%
          </span>
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="border-t px-3 pb-2">
        <TabellaAttributi righe={righe} />
      </CollapsibleContent>
    </Collapsible>
  );
}

function TabellaAttributi({ righe }: { righe: AttributoCarico[] }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Attributo</TableHead>
          <TableHead className="text-right">UA</TableHead>
          <TableHead className="text-right">%</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {righe.map((x) => (
          <TableRow key={x.attributoId}>
            <TableCell>{x.nome}</TableCell>
            <TableCell className="text-right tabular-nums">{x.ua}</TableCell>
            <TableCell className="text-right tabular-nums">{Math.round(x.percentuale)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
